import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Instagram, Send, Target, ChevronDown } from "lucide-react";
import { SOCIALS } from "@/data/mock";
import { useSiteConfig } from "@/context/SiteConfig";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.4 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const cfg = useSiteConfig();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const mangoY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const mangoScale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollTo = (id) =>
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="inicio"
      ref={ref}
      className="relative min-h-[100svh] flex items-center overflow-hidden pt-24 pb-16"
      data-testid="hero-section"
    >
      {/* Mango symbol with parallax + glow */}
      <motion.div
        style={{ y: mangoY, scale: mangoScale, opacity: fade }}
        className="absolute right-[-6%] top-[14%] md:right-[4%] md:top-[16%] z-0 pointer-events-none"
      >
        <div className="relative animate-float-slow">
          <div className="absolute inset-0 -m-24 radial-gold blur-2xl" />
          <img
            src="/manga-symbol.png"
            alt=""
            className="relative w-[280px] md:w-[440px] lg:w-[520px] object-contain gold-glow opacity-90"
          />
        </div>
      </motion.div>

      {/* faint grid glow */}
      <div className="absolute left-[-10%] top-[30%] w-[420px] h-[420px] radial-gold blur-3xl z-0" />

      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8"
          data-testid="hero-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF41] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00FF41]" />
          </span>
          <span className="text-xs tracking-[0.2em] uppercase text-white/70 font-medium">
            Sistema Online • Sinais em Tempo Real
          </span>
        </motion.div>

        <h1 className="font-display font-black tracking-tighter uppercase leading-[0.92] text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
          {["Monitoramento", "de Sinais em", "Tempo Real"].map((t, i) => (
            <span key={i} className="block overflow-hidden py-1">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className={`block ${i === 2 ? "text-gold-gradient" : "text-white"}`}
              >
                {t}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-7 max-w-xl text-base md:text-lg text-white/60 leading-relaxed font-body"
        >
          Acompanhe estatísticas, sinais e indicadores de forma organizada e
          intuitiva. Conteúdo exclusivamente informativo.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <button
            onClick={() => scrollTo("#sinais")}
            className="btn-gold font-display font-bold px-8 py-4 rounded-full flex items-center gap-2 hover:scale-105 transition-transform duration-300"
            data-testid="hero-cta-signal"
          >
            <Target className="w-5 h-5" /> Gerar Sinal Agora
          </button>

          <div className="flex items-center gap-3">
            <a
              href={cfg.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 px-5 py-3.5 rounded-full glass-card hover:border-[#F5C542]/40 transition-colors duration-300"
              data-testid="hero-instagram"
            >
              <Instagram className="w-5 h-5 text-[#F5C542]" />
              <span className="font-medium text-sm text-white/80 group-hover:text-white">
                Instagram
              </span>
            </a>
            <a
              href={cfg.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 px-5 py-3.5 rounded-full glass-card hover:border-[#F5C542]/40 transition-colors duration-300"
              data-testid="hero-telegram"
            >
              <Send className="w-5 h-5 text-[#F5C542]" />
              <span className="font-medium text-sm text-white/80 group-hover:text-white">
                Telegram
              </span>
            </a>
          </div>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("#rtp")}
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40 hover:text-[#F5C542] transition-colors"
        aria-label="Rolar"
        data-testid="hero-scroll-down"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Explorar</span>
        <ChevronDown className="w-5 h-5 animate-bounce" />
      </motion.button>
    </section>
  );
};
